import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AUTH_TOKEN, REFRESH_TOKEN, AUTH_USER } from 'constants/AuthConstant';
import AuthService from 'services/AuthService';

const readUser = () => {
	try {
		return JSON.parse(localStorage.getItem(AUTH_USER)) || null
	} catch (e) {
		return null
	}
}

export const initialState = {
	loading: false,
	message: '',
	showMessage: false,
	redirect: '',
	token: localStorage.getItem(AUTH_TOKEN) || null,
	user: readUser(),
}

const persist = (data) => {
	if (data.access) localStorage.setItem(AUTH_TOKEN, data.access)
	if (data.refresh) localStorage.setItem(REFRESH_TOKEN, data.refresh)
	if (data.user) localStorage.setItem(AUTH_USER, JSON.stringify(data.user))
}

const clearPersist = () => {
	localStorage.removeItem(AUTH_TOKEN)
	localStorage.removeItem(REFRESH_TOKEN)
	localStorage.removeItem(AUTH_USER)
}

const errText = (err) => {
	const data = err?.response?.data
	if (!data) return 'Unable to reach the server'
	if (data.message) return data.message
	if (typeof data.detail === 'string') return data.detail
	if (data.errors && typeof data.errors === 'object') {
		const first = Object.values(data.errors)[0]
		return Array.isArray(first) ? first[0] : String(first)
	}
	return 'Request failed'
}

export const signIn = createAsyncThunk('auth/signIn', async (data, { rejectWithValue }) => {
	try {
		const resp = await AuthService.login({ email: data.email, password: data.password })
		persist(resp)
		return resp
	} catch (err) {
		return rejectWithValue(errText(err))
	}
})

export const signUp = createAsyncThunk('auth/signUp', async (data, { rejectWithValue }) => {
	try {
		const payload = {
			email: data.email,
			first_name: data.first_name,
			last_name: data.last_name,
			phone: data.phone || '',
			password: data.password,
			password_confirm: data.confirm || data.password_confirm,
		}
		const resp = await AuthService.register(payload)
		return resp.message || 'Registration received. Your account is pending staff approval.'
	} catch (err) {
		return rejectWithValue(errText(err))
	}
})

export const signOut = createAsyncThunk('auth/signOut', async () => {
	clearPersist()
	return null
})

export const loadUser = createAsyncThunk('auth/loadUser', async (_, { rejectWithValue }) => {
	try {
		const user = await AuthService.me()
		localStorage.setItem(AUTH_USER, JSON.stringify(user))
		return user
	} catch (err) {
		return rejectWithValue(errText(err))
	}
})

export const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		authenticated: (state, action) => {
			state.loading = false
			state.redirect = '/'
			state.token = action.payload
		},
		showAuthMessage: (state, action) => {
			state.message = action.payload
			state.showMessage = true
			state.loading = false
		},
		hideAuthMessage: (state) => {
			state.message = ''
			state.showMessage = false
		},
		signOutSuccess: (state) => {
			state.loading = false
			state.token = null
			state.user = null
			state.redirect = '/'
		},
		showLoading: (state) => {
			state.loading = true
		},
		signInSuccess: (state, action) => {
			state.loading = false
			state.token = action.payload
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(signIn.pending, (state) => { state.loading = true })
			.addCase(signIn.fulfilled, (state, action) => {
				state.loading = false
				state.redirect = '/'
				state.token = action.payload.access
				state.user = action.payload.user
			})
			.addCase(signIn.rejected, (state, action) => {
				state.message = action.payload
				state.showMessage = true
				state.loading = false
			})
			.addCase(signOut.fulfilled, (state) => {
				state.loading = false
				state.token = null
				state.user = null
				state.redirect = '/'
			})
			.addCase(signUp.pending, (state) => { state.loading = true })
			.addCase(signUp.fulfilled, (state, action) => {
				state.loading = false
				state.message = action.payload
				state.showMessage = true
			})
			.addCase(signUp.rejected, (state, action) => {
				state.message = action.payload
				state.showMessage = true
				state.loading = false
			})
			.addCase(loadUser.fulfilled, (state, action) => {
				state.user = action.payload
			})
	},
})

export const {
	authenticated,
	showAuthMessage,
	hideAuthMessage,
	signOutSuccess,
	showLoading,
	signInSuccess,
} = authSlice.actions

export default authSlice.reducer
